import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { AlertCircle, Loader2, ArrowLeft, Search, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Fallback rates — used when the API returns empty */
const FALLBACK_RATES = [
  { lbs: 1,  price: 600   },
  { lbs: 2,  price: 1000  },
  { lbs: 3,  price: 1400  },
  { lbs: 4,  price: 1750  },
  { lbs: 5,  price: 2100  },
  { lbs: 6,  price: 2450  },
  { lbs: 7,  price: 2800  },
  { lbs: 8,  price: 3150  },
  { lbs: 9,  price: 3500  },
  { lbs: 10, price: 3850  },
];

/* Normalise every plausible Kanal response shape into a flat array */
function parseTiers(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  const candidates = [
    raw.rate_table, raw.tiers, raw.rates, raw.data, raw.items, raw.results,
    raw.courier_rates, raw.rate_list, raw.rateList, raw.price_list,
    raw.priceList, raw.courier_rate, raw.shipping_rates,
    raw.payload, raw.list, raw.rate_data,
  ];
  for (const c of candidates) {
    if (Array.isArray(c) && c.length > 0) return c;
  }
  const vals = Object.values(raw);
  if (vals.length > 0 && vals.every((v) => v && typeof v === "object" && !Array.isArray(v)))
    return vals;
  for (const val of vals) {
    if (Array.isArray(val) && val.length > 0) return val;
  }
  return [];
}

function tierLabel(tier, i) {
  const w = tier.lbs ?? tier.weight ?? tier.weight_lbs ?? tier.weight_lb ?? tier.lb ?? tier.pounds;
  if (tier.min_weight !== undefined && tier.max_weight !== undefined)
    return `${tier.min_weight} – ${tier.max_weight} ${tier.unit || "lb"}`;
  return tier.weight_range || tier.range || tier.label
    || (w !== undefined ? `${w} lb${Number(w) !== 1 ? "s" : ""}` : `Tier ${i + 1}`);
}

function tierPrice(tier) {
  const raw = tier.price ?? tier.rate ?? tier.amount ?? tier.cost ?? tier.Price ?? 0;
  const num = parseFloat(String(raw).replace(/[^0-9.]/g, "")) || 0;
  return num.toLocaleString("en-JM", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function RatesPage() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/rates`)
      .then(({ data }) => {
        console.log("[KYo public rates] raw:", data);
        const parsed = parseTiers(data);
        if (parsed.length > 0) {
          setTiers(parsed);
        } else {
          setTiers(FALLBACK_RATES);
          setUsingFallback(true);
        }
      })
      .catch((err) => {
        console.log("[KYo public rates] error:", err?.response?.status, err?.response?.data);
        setError("Could not load live rates. Showing standard KYo rates.");
        setTiers(FALLBACK_RATES);
        setUsingFallback(true);
      })
      .finally(() => setLoading(false));
  }, []);

  /* Filter tiers by query — matches weight label or raw weight number */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tiers;
    return tiers.filter((tier, i) => tierLabel(tier, i).toLowerCase().includes(q));
  }, [tiers, query]);

  /* Rows to display: always show all when query is active or showAll is true */
  const DEFAULT_VISIBLE = 10;
  const isFiltering = query.trim().length > 0;
  const visibleTiers = (showAll || isFiltering) ? filtered : filtered.slice(0, DEFAULT_VISIBLE);
  const hasMore = !isFiltering && filtered.length > DEFAULT_VISIBLE;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <main className="flex-1 pt-24 pb-20 px-5 lg:px-6">
        <div className="max-w-[720px] mx-auto">

          {/* Back link */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-[13px] text-gray-400 hover:text-[#ED2E24] transition-colors mb-8"
            data-testid="rates-back-btn"
          >
            <ArrowLeft size={14} />
            Back
          </button>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1a1a1a] leading-tight">
              Shipping Rates
            </h1>
            <div className="mt-2 w-12 h-1 bg-[#ED2E24] rounded-full" />
            <p className="mt-5 text-[15px] text-gray-500 leading-relaxed max-w-prose">
              All prices are in <strong>Jamaican Dollars (JMD)</strong>. Rates are based on the
              weight of your shipment in pounds (lbs) and apply to Air shipping via the
              KYo Miami Warehouse. Rates are subject to change — contact KYo for a custom quote.
            </p>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-20 text-gray-400 gap-2.5" data-testid="rates-loading">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-[14px]">Loading rates…</span>
            </div>
          )}

          {/* Notice banner (fallback / error) */}
          {!loading && (error || usingFallback) && (
            <div className="mb-5 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="rates-fallback-notice">
              <AlertCircle size={14} className="text-amber-500 mt-0.5 shrink-0" />
              <p className="text-[13px] text-amber-700">
                {error || "Showing standard KYo rates. Live rates will update automatically."}
              </p>
            </div>
          )}

          {/* Search input */}
          {!loading && tiers.length > 0 && (
            <div className="relative mb-4" data-testid="rates-search-container">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowAll(false); }}
                placeholder="Filter by weight, e.g. 25"
                className="w-full border border-gray-200 rounded-xl pl-10 pr-10 py-3 text-[14px] text-gray-900 bg-white outline-none focus:border-[#ED2E24] transition-colors shadow-sm placeholder:text-gray-400"
                data-testid="rates-search-input"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full px-2 py-0.5"
                  data-testid="rates-search-clear"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Rates table */}
          {!loading && tiers.length > 0 && (
            <>
              <div className="rounded-2xl border border-gray-100 overflow-hidden shadow-sm" data-testid="rates-table-container">
                <table className="w-full text-[14px]" data-testid="public-rates-table">
                  <thead>
                    <tr className="bg-[#1a1a1a] text-white">
                      <th className="text-left px-6 py-4 font-semibold text-[13px] uppercase tracking-widest">
                        Weight
                      </th>
                      <th className="text-right px-6 py-4 font-semibold text-[13px] uppercase tracking-widest">
                        Price (JMD)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTiers.length > 0 ? visibleTiers.map((tier, i) => (
                      <tr
                        key={i}
                        className={`border-b border-gray-50 transition-colors ${
                          i % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                        } hover:bg-red-50/30`}
                        data-testid={`rate-row-${i}`}
                      >
                        <td className="px-6 py-3.5 font-medium text-gray-800">
                          {tierLabel(tier, tiers.indexOf(tier))}
                        </td>
                        <td className="px-6 py-3.5 text-right font-semibold text-gray-900">
                          J${tierPrice(tier)}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={2} className="px-6 py-10 text-center text-[14px] text-gray-400" data-testid="rates-no-results">
                          No rates found for &ldquo;{query}&rdquo;
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t border-gray-100">
                      <td colSpan={2} className="px-6 py-3 text-[12px] text-gray-400">
                        {isFiltering
                          ? `${filtered.length} result${filtered.length !== 1 ? "s" : ""} · `
                          : `Showing ${visibleTiers.length} of ${tiers.length} tiers · `}
                        Prices in Jamaican Dollars (J$) · Air shipping via KYo Miami Warehouse
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Show all / collapse toggle */}
              {hasMore && (
                <button
                  onClick={() => setShowAll((s) => !s)}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 text-[13px] font-medium text-gray-600 hover:border-gray-300 hover:text-gray-800 transition-colors"
                  data-testid="rates-show-all-btn"
                >
                  {showAll ? (
                    <><ChevronUp size={15} /> Show fewer rates</>
                  ) : (
                    <><ChevronDown size={15} /> Show all {tiers.length} rates</>
                  )}
                </button>
              )}
            </>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
}
